import {
	AddToCrmFormValues,
	CrmEntityType,
	CrmProvider,
} from "@/pages/Private/redux/crm/crm.schema";

export type AddToCrmEntityKey = keyof AddToCrmFormValues;

/**
 * Providers whose sync always writes the contact **and** the account it hangs off, so neither can
 * be deselected — `CRM_ADD_ENTITY_RULES` in `apps/api/src/crm/crm-add-provider.rules.ts` rejects
 * the request outright otherwise.
 *
 * Salesforce is on this list for its own reason rather than by analogy: an `Opportunity` carries
 * no contact field at all (the link is the `OpportunityContactRole` junction) and hangs off
 * `AccountId`, so a deal needs both records to exist. A standalone Account write is deliberately
 * out of v1.
 */
export const REQUIRES_CONTACT_AND_ACCOUNT: readonly CrmProvider[] = [
	CrmProvider.PIPEDRIVE,
	CrmProvider.MSDYNAMICS,
	CrmProvider.SALESFORCE,
];

/** The order the entity checkboxes render in, per provider. */
export const PROVIDER_ENTITY_ORDER: Record<CrmProvider, AddToCrmEntityKey[]> = {
	[CrmProvider.HUBSPOT]: ["contact", "account", "lead", "deal"],
	[CrmProvider.ZOHO]: ["lead", "contact", "account", "deal"],
	[CrmProvider.PIPEDRIVE]: ["contact", "account", "lead", "deal"],
	[CrmProvider.MSDYNAMICS]: ["contact", "account", "lead", "deal"],
	[CrmProvider.SALESFORCE]: ["contact", "account", "lead", "deal"],
};

const requiresContactAndAccount = (provider: CrmProvider): boolean =>
	REQUIRES_CONTACT_AND_ACCOUNT.includes(provider);

/** What the picker opens with — already a combination the backend accepts. */
export const getDefaultValues = (provider?: CrmProvider): AddToCrmFormValues => ({
	contact: provider !== CrmProvider.ZOHO,
	account: provider ? requiresContactAndAccount(provider) : false,
	lead: provider === CrmProvider.ZOHO,
	deal: false,
});

/** The selected checkboxes as the entity list the request carries. */
export const toEntities = (values: AddToCrmFormValues): CrmEntityType[] =>
	(
		[
			[CrmEntityType.CONTACT, values.contact],
			[CrmEntityType.ACCOUNT, values.account],
			[CrmEntityType.LEAD, values.lead],
			[CrmEntityType.DEAL, values.deal],
		] as Array<[CrmEntityType, boolean]>
	)
		.filter(([, selected]) => selected)
		.map(([entity]) => entity);

/**
 * Apply one checkbox toggle under the provider's entity rules, returning the whole next selection.
 *
 * The point of resolving this here rather than in the modal is that the picker must make an
 * invalid combination **unreachable** instead of relying on the backend's 400. Every rule below
 * mirrors one branch of `CRM_ADD_ENTITY_RULES`; a combination this function can produce is a
 * combination the API accepts.
 */
export const applyEntityRule = (
	provider: CrmProvider,
	values: AddToCrmFormValues,
	name: AddToCrmEntityKey,
	selected: boolean
): AddToCrmFormValues => {
	const next: AddToCrmFormValues = { ...values, [name]: selected };

	if (provider === CrmProvider.HUBSPOT && name === "deal" && selected) {
		next.contact = true;
		next.account = true;
	}

	if (provider === CrmProvider.ZOHO) {
		if (name === "lead" && selected) {
			next.contact = false;
			next.account = false;
			next.deal = false;
		}

		if ((name === "contact" || name === "account") && selected) {
			next.lead = false;
		}

		if (name === "deal" && selected) {
			next.lead = false;
			next.contact = true;
			next.account = true;
		}
	}

	if (requiresContactAndAccount(provider)) {
		// Contact + account are not optional, so a toggle can never clear them.
		next.contact = true;
		next.account = true;

		if (name === "lead" && selected) {
			next.deal = false;
		}

		if (name === "deal" && selected) {
			next.lead = false;
		}
	}

	return next;
};

/** Whether a checkbox is locked — the selection is mandatory or implied by another choice. */
export const isEntityLocked = (
	provider: CrmProvider,
	values: AddToCrmFormValues,
	name: AddToCrmEntityKey
): boolean => {
	const isContactOrAccount = name === "contact" || name === "account";

	if (requiresContactAndAccount(provider) && isContactOrAccount) {
		return true;
	}

	return (
		values.deal && [CrmProvider.HUBSPOT, CrmProvider.ZOHO].includes(provider) && isContactOrAccount
	);
};
